import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Calendar, Search, LogOut, UserCircle, Crown } from 'lucide-react';
import DoctorSearchComponent from '@/components/mr/DoctorSearch';
import MRAppointments from '@/components/mr/MRAppointments';
import MRProfile from '@/components/mr/MRProfile';
import Subscription from '@/components/mr/Subscription';

export default function MRDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('search');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const tabs = [
    { id: 'search', label: 'Search', icon: Search },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'subscription', label: 'Subscription', icon: Crown },
    { id: 'profile', label: 'Profile', icon: UserCircle },
  ];

  return (
    <div className="min-h-screen bg-background-subtle overflow-x-hidden">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="h-8 w-8 sm:h-10 sm:w-10 bg-primary rounded-full flex items-center justify-center">
              <Calendar className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-heading font-bold text-background-dark">MR Dashboard</h1>
              <p className="text-xs sm:text-sm text-slate-600 truncate max-w-[120px] sm:max-w-none">Welcome, {user?.full_name}</p>
            </div>
          </div>
          <Button
            data-testid="mr-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="text-slate-600 hover:text-destructive px-2 sm:px-4"
          >
            <LogOut className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {/* Navigation Tabs - Scrollable on mobile */}
      <div className="bg-white border-b border-slate-200 sticky top-16 sm:top-20 z-30">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-12">
          <div className="flex overflow-x-auto scrollbar-hide -mx-2 px-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`mr-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium transition-colors border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-primary border-primary'
                      : 'text-slate-600 border-transparent hover:text-primary'
                  }`}
                >
                  <Icon className="inline-block mr-1 sm:mr-2 h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12 py-4 sm:py-12">
        {activeTab === 'search' && <DoctorSearchComponent />}
        {activeTab === 'appointments' && <MRAppointments />}
        {activeTab === 'subscription' && <Subscription />}
        {activeTab === 'profile' && <MRProfile />}
      </main>
    </div>
  );
}